// Frontend/src/components/admin/AnnouncementsManager.jsx
import { format } from 'date-fns';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useAnnouncements } from '../../contexts/AnnouncementsContext';
import { useCampuses } from '../../contexts/CampusContext';
import { useCellMinistries } from '../../contexts/CellMinistryContext';
import { useMinistries } from '../../contexts/MinistryContext';
import AnnouncementService from '../../services/announcementsService';
import AnnouncementForm from './forms/AnnouncementForm';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import DataTable from './ui/DataTable';
import Modal from './ui/Modal';

const AnnouncementsManager = () => {
  const { announcements, loading, error, setAnnouncements } = useAnnouncements();
  const { campuses } = useCampuses(); 
  const { cellGroups } = useCellMinistries();
  const { ministries } = useMinistries(); 
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'

  // Filter announcements based on search term
  const filteredAnnouncements = announcements.filter(announcement =>
    announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    announcement.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    announcement.targetType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { 
      field: 'profile_picture', 
      header: 'Image', 
      width: '80px',
      render: (row) => (
        <div className="flex justify-center">
          <div className="relative group">
            <img 
              className="h-14 w-14 rounded-xl object-cover border-2 border-white shadow-lg ring-2 ring-blue-100 group-hover:ring-blue-300 transition-all duration-300" 
              src={row.imageUrl} 
              alt={row.title}
              onError={(e) => {
                e.target.src = '/api/placeholder/56/56';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
        </div>
      )
    },
    { 
      field: 'title', 
      header: 'Title',
      width: '25%',
      render: (row) => (
        <div className="space-y-1">
          <div className="font-semibold text-gray-900 truncate">{row.title}</div>
          <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full inline-block">
            {row.targetType.charAt(0).toUpperCase() + row.targetType.slice(1)}
          </div>
        </div>
      )
    },
    { 
      field: 'description', 
      header: 'Description',
      width: '35%',
      render: (row) => (
        <div className="text-sm text-gray-600 line-clamp-3 leading-relaxed">
          {row.description}
        </div>
      )
    },
    { 
      field: 'publishDate', 
      header: 'Publish Date',
      width: '15%',
      render: (row) => (
        <div className="text-center">
          <div className="text-sm font-medium text-gray-900">
            {format(new Date(row.publishDate), 'MMM dd')}
          </div>
          <div className="text-xs text-gray-500">
            {format(new Date(row.publishDate), 'yyyy')}
          </div>
        </div>
      )
    },
    {
      field: 'actions',
      header: 'Actions',
      width: '15%',
      render: (row) => (
        <div className="flex space-x-2">
          <button 
            onClick={() => handleEdit(row)}
            className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200"
          >
            Edit
          </button>
          <button 
            onClick={() => handleDeleteClick(row)}
            className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 transition-all duration-200"
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  const handleDeleteClick = (announcement) => {
    setCurrentAnnouncement(announcement);
    setIsDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      setProcessing(true);
      await AnnouncementService.deleteAnnouncement(currentAnnouncement.id);
      setAnnouncements(announcements.filter(item => item.id !== currentAnnouncement.id));
      toast.success('Announcement deleted successfully');
    } catch (error) {
      toast.error(`Failed to delete announcement: ${error.message}`);
    } finally {
      setProcessing(false);
      setIsDeleteConfirmOpen(false);
    }
  };

  const handleEdit = (announcement) => {
    setCurrentAnnouncement(announcement);
    setIsModalOpen(true);
  };

  const handleSubmit = async (formData) => {
    try {
      setProcessing(true);
      let updatedAnnouncements;
      
      if (currentAnnouncement) {
        const updated = await AnnouncementService.updateAnnouncement(currentAnnouncement.id, formData);
        updatedAnnouncements = announcements.map(a => 
          a.id === currentAnnouncement.id ? updated : a
        );
        toast.success('Announcement updated successfully');
      } else {
        const created = await AnnouncementService.createAnnouncement(formData);
        updatedAnnouncements = [...announcements, created];
        toast.success('Announcement created successfully');
      }
      
      setAnnouncements(updatedAnnouncements);
      setIsModalOpen(false);
    } catch (error) {
      toast.error(`Failed to save announcement: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const campusOptions = campuses?.map(m => ({
    value: m.name.toLowerCase(),
    label: m.name
  })) || [];

  const ministryOptions = ministries?.map(m => ({
    value: m.name.toLowerCase(),
    label: m.name
  })) || [];

  const cellGroupOptions = cellGroups?.map(m => ({
    value: m.name.toLowerCase(),
    label: m.name
  })) || [];

  // Card view component
  const AnnouncementCard = ({ announcement }) => (
    <div className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-200 overflow-hidden">
      <div className="relative">
        <img 
          className="w-full h-48 object-cover" 
          src={announcement.imageUrl} 
          alt={announcement.title}
          onError={(e) => {
            e.target.src = '/api/placeholder/400/200';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute top-4 right-4">
          <span className="px-3 py-1 text-xs font-medium text-white bg-black/50 backdrop-blur-sm rounded-full">
            {announcement.targetType.charAt(0).toUpperCase() + announcement.targetType.slice(1)}
          </span>
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-white font-bold text-lg line-clamp-2">{announcement.title}</h3>
        </div>
      </div>
      <div className="p-6">
        <p className="text-gray-600 text-sm line-clamp-3 mb-4">{announcement.description}</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {format(new Date(announcement.publishDate), 'MMM dd, yyyy')}
          </span>
          <div className="flex space-x-2">
            <button 
              onClick={() => handleEdit(announcement)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
              title="Edit"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button 
              onClick={() => handleDeleteClick(announcement)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
              title="Delete"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with glassmorphism */}
        <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-xl border border-white/20 p-8 mb-8">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Announcements
              </h1>
              <p className="text-gray-600 mt-2">Manage and organize your announcements</p>
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  {filteredAnnouncements.length} active announcements
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search Bar */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search announcements..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-3 w-64 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm"
                />
              </div>
              
              {/* View Toggle */}
              <div className="flex bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'table' 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  Table
                </button>
                <button
                  onClick={() => setViewMode('card')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'card' 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  Cards
                </button>
              </div>
              
              {/* Create Button */}
              <Button 
                onClick={() => {
                  setCurrentAnnouncement(null);
                  setIsModalOpen(true);
                }}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create New
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-800">{error.message || 'Failed to load announcements'}</span>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-xl border border-white/20 overflow-hidden">
          {viewMode === 'table' ? (
            <DataTable
              data={filteredAnnouncements}
              columns={columns}
              loading={loading}
              emptyMessage="No announcements found"
            />
          ) : (
            <div className="p-8">
              {filteredAnnouncements.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredAnnouncements.map((announcement) => (
                    <AnnouncementCard key={announcement.id} announcement={announcement} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 011 1v1a1 1 0 01-1 1v9a2 2 0 01-2 2H6a2 2 0 01-2-2V7a1 1 0 01-1-1V5a1 1 0 011-1h4zM9 3v1h6V3H9zM6 7v11h12V7H6z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No announcements found</h3>
                  <p className="text-gray-500">
                    {searchTerm ? 'Try adjusting your search terms' : 'Create your first announcement to get started'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modals */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={currentAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
          size="lg"
        >
          <AnnouncementForm
            initialValues={currentAnnouncement || {
              title: '',
              description: '',
              publishDate: new Date(),
              targetType: 'all',
              imageUrl: '',
              campus: "",
              ministry: "",
              cellgroup: ""
            }}
            onSubmit={handleSubmit}
            onCancel={() => setIsModalOpen(false)}
            isSubmitting={processing}
            campusesOptions={campusOptions}
            ministriesOptions={ministryOptions}
            cellGroupsOptions={cellGroupOptions}
          />
        </Modal>

        <ConfirmDialog
          isOpen={isDeleteConfirmOpen}
          onClose={() => setIsDeleteConfirmOpen(false)}
          onConfirm={handleDelete}
          title="Confirm Delete"
          message={`Are you sure you want to delete "${currentAnnouncement?.title}" announcement?`}
          confirmText="Delete"
          cancelText="Cancel"
          processing={processing}
        />
      </div>
    </div>
  );
};

export default AnnouncementsManager;